import React, { useState, useEffect, useRef } from "react";
import Button from "../../../components/common/button";
import Logo from "../../../components/common/logo";
import MultipleDropdown from '../../../components/common/multipleDropdown';
import Dropdown from '../../../components/common/dropdown';
import rolesData from '../common/roles.json';
import businessAreasData from '../common/business-areas.json';
import { useUsers } from '../../../hooks/useUsers';
import { useOrganization } from '../../../hooks/useOrganization';
import ErrorPopup from '../../../components/common/errorPopup';
import OnboardingCard from "../../../components/common/OnboardingCard";
import Header from "../../../components/common/Header";
import PreviewCard from "../../../components/common/PreviewCard";

interface UserRoleProps {
  onNext: () => void;
  onBack: () => void;
  currentStep: number;
  totalSteps: number;
  initialData: {
    role: string;
    role_other: string;
    businessAreas: string[];
  };
}

interface Option {
  value: string;
  label: string;
}

interface MultiSelectDropdownProps {
  options: Option[];
  value: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  className?: string;
  allowOther?: boolean;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  value = [],
  onChange,
  placeholder = "Select options",
  className = "",
  allowOther = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [otherValue, setOtherValue] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const otherInputRef = useRef<HTMLInputElement>(null);

  const handleOtherInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && otherValue.trim()) {
      e.preventDefault();
      // Add the "other" value to the front of the list
      onChange([otherValue.trim(), ...value]);
      setOtherValue('');
    }
  };

  const handleRemoveTag = (tagValue: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newValues = value.filter(v => v !== tagValue);
    onChange(newValues);
  };

  const handleOptionClick = (optionValue: string) => {
    const newValues = value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      : [optionValue, ...value.filter(v => v !== optionValue)];
    onChange(newValues);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`relative cursor-pointer shadow-[0px_0px_0px_1px_#EEEFF1_inset] text-left flex items-center justify-between h-[40px] px-4 rounded-[10px] bg-white ${className}`}
      >
        <div className="flex-1 flex items-center max-w-[calc(100%-24px)]">
          <div className="w-full overflow-x-auto scrollbar-hide scroll-smooth">
            {value.length > 0 ? (
              <div className="flex flex-nowrap gap-1.5 items-center py-1">
                {value.map((val) => {
                  const option = options.find(opt => opt.value === val);
                  return (
                    <span
                      key={val}
                      className="bg-[#EEEFF1] text-[#222222] px-1.5 py-0.5 rounded-[4px] flex items-center gap-1 text-xs whitespace-nowrap"
                    >
                      {option?.label || val}
                      <button
                        onClick={(e) => handleRemoveTag(val, e)}
                        className="text-[#7A7A7A] hover:text-[#222222] transition-colors text-xs"
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
              </div>
            ) : (
              <span className="text-[#DDDDDD] text-sm">{placeholder}</span>
            )}
          </div>
        </div>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''} text-[#7A7A7A] ml-2 flex-shrink-0`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 text-xs font-normal bg-[#FFFFFF] text-left border border-[#EEEFF1] rounded-lg shadow-lg">
          <div className="py-1 max-h-[180px] overflow-y-auto scrollbar-hide">
            {options.map((option) => (
              <div
                key={option.value}
                onClick={() => handleOptionClick(option.value)}
                className={`px-3 py-2 cursor-pointer ${
                  value.includes(option.value)
                    ? 'bg-[#EEEFF1] text-[#222222]'
                    : 'hover:bg-[#FAFAFA] text-[#222222]'
                }`}
              >
                {option.label}
              </div>
            ))}
            {allowOther && (
              <div className="px-3 py-2">
                <input
                  ref={otherInputRef}
                  type="text"
                  value={otherValue}
                  onChange={(e) => setOtherValue(e.target.value)}
                  onKeyDown={handleOtherInputKeyDown}
                  placeholder="Enter custom option"
                  className="w-full px-2 py-1 text-sm border border-[#EEEFF1] rounded
                    focus:outline-none focus:ring-1 focus:ring-[#29DC5B] focus:border-transparent"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const UserRole: React.FC<UserRoleProps> = ({
  onNext,
  onBack,
  currentStep,
  totalSteps,
  initialData
}) => {
  const { user, updateUser, isUpdating: isUpdatingUser } = useUsers();
  const { organization, updateOrganization, isUpdating: isUpdatingOrg } = useOrganization();
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    role: initialData.role || '',
    role_other: initialData.role_other || '',
    businessAreas: initialData.businessAreas || []
  });
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (user && organization) {
      const roleValue = user.role || '';
      const roleExists = rolesData.roles.some(
        role => role.toLowerCase() === roleValue.toLowerCase()
      );

      setFormData(prev => ({
        ...prev,
        role: roleExists ? roleValue : 'other',
        role_other: !roleExists ? roleValue : '',
        businessAreas: organization.business_areas || []
      }));
    }
  }, [user, organization]);

  const handleInputChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const isFormValid = () => {
    if (formData.role === 'other' && !formData.role_other) {
      return false;
    }
    return formData.role.trim() !== '' && formData.businessAreas.length > 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isFormValid()) {
      try {
        setError('');
        
        const userRole = formData.role === 'other' ? formData.role_other : formData.role;
        await updateUser({
          role: userRole
        });

        await updateOrganization({
          business_areas: formData.businessAreas
        });

        onNext();

      } catch (error) {
        console.error('Failed to update user details:', error);
        setErrorMessage('Failed to save your preferences. Please try again.');
        setShowError(true);
      }
    }
  };

  useEffect(() => {
    const handleKeyDown = async (event: KeyboardEvent) => {
      // Get the active element
      const activeElement = document.activeElement;
      
      // Check if any dropdown is open
      const dropdownsOpen = document.querySelector('.absolute.z-10') !== null;
      
      // Check if we're currently editing an "other" input
      const isEditingOther = activeElement?.tagName === 'INPUT' && 
        (activeElement.closest('.dropdown-other-input'));

      if (event.key === 'Escape') {
        try {
          const userRole = formData.role === 'other' ? formData.role_other : formData.role;
          
          // Update user role
          if (userRole) {
            await updateUser({
              role: userRole
            });
          }

          // Update business areas
          if (formData.businessAreas.length > 0) {
            await updateOrganization({
              business_areas: formData.businessAreas
            });
          }
        } catch (error) {
          console.error('Failed to save data on back:', error);
        }
        onBack();
      } else if (event.key === 'Enter' && !dropdownsOpen && !isEditingOther && isFormValid()) {
        handleSubmit({ preventDefault: () => {} } as React.FormEvent);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onBack, handleSubmit, isFormValid, formData, updateUser, updateOrganization]);

  const roleOptions = rolesData.roles.map(role => ({ value: role, label: role }));

  const businessAreaOptions = businessAreasData.business_areas.map(area => ({ value: area, label: area }));

  const handleBack = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Save form state if needed
    const formState = {
      role: formData.role,
      role_other: formData.role_other,
      businessAreas: formData.businessAreas,
    };

    // Call onBack after a short delay to ensure state is saved
    setTimeout(() => {
      onBack();
    }, 0);
  };

  const leftPanelContent = (
    <div className="flex flex-col h-full">
      <div className="flex flex-col px-[16px] pt-[4px] pb-[16px]">
        <div className="mb-1">
          <div className="text-[32px] font-semibold mb-[36px] text-left">
            Help us understand your needs!
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col">
            <div className="space-y-3">
              {/* Role Dropdown */}
              <div className="relative">
                <label className="text-sm font-normal text-[#75777C] mb-2 block text-left">
                  What is your role?
                </label>
                <Dropdown
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  options={roleOptions}
                  placeholder="Select the role"
                  allowOther={true}
                  otherValue={formData.role_other}
                  onOtherChange={(value) => {
                    setFormData(prev => ({
                      ...prev,
                      role_other: value
                    }));
                  }}
                  className="w-full h-[40px] pl-4 pr-4 text-left rounded-[10px] text-[#222222] text-sm placeholder:text-[#9FA1A7] focus:outline-none focus:ring-1 focus:ring-[#29DC5B] focus:border-transparent shadow-[0px_0px_2px_0px_#EEEFF1]"
                />
              </div>

              {/* Business Areas Dropdown */}
              <div className="relative">
                <label className="text-sm font-normal text-[#75777C] mb-2 block text-left">
                  Select the business area for Clodexa use
                </label>
                <MultiSelectDropdown
                  options={businessAreaOptions}
                  value={formData.businessAreas}
                  onChange={(newValues) => {
                    setFormData(prev => ({
                      ...prev,
                      businessAreas: newValues
                    }));
                  }}
                  placeholder="Select business areas"
                  allowOther={true}
                  className="w-full"
                />
              </div>
            </div>

            <div className="flex mt-[28px]">
              <button
                type="submit"
                className="w-full h-[40px] bg-[#29DC5B] text-[#222222] rounded-[10px] flex items-center justify-center text-sm font-normal hover:bg-[#22c55e] transition-colors shadow-[0px_0px_0px_1px_#2325291A_inset] disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isUpdatingUser || isUpdatingOrg || !isFormValid()}
              >
                {(isUpdatingUser || isUpdatingOrg) ? 'Saving...' : 'Continue'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col items-center">
      <OnboardingCard 
        leftPanel={leftPanelContent} 
        rightPanel={<PreviewCard 
          variant="preview" 
          showRole={true} 
          role={formData.role === 'other' ? formData.role_other : formData.role}
          companyName={organization?.linkedin_company_profile?.name || organization?.name}
        />}
        pageInfo={{
          currentPage: currentStep + 1,
          totalPages: totalSteps
        }}
      />
      
      <ErrorPopup
        isOpen={showError}
        onClose={() => setShowError(false)}
        message={errorMessage}
      />
    </div>
  );
};

export default UserRole;
